import { useAppSelector } from "app/hooks";
import { useEffect, useState } from "react";
import CalibrationApi from "./calibrationApi";
import { notNullNorUndefined } from "shared/helpers/notNullNorUndefined";
import CalibrateForm from "./CalibrationForm";
import DataItem from "shared/components/DataItem";
import { normaliseValue } from "shared/helpers/unitHelpers";
import { roundNumber } from "shared/helpers/numberHelpers";
import Spinner from "shared/components/Spinner";

type CalibrationValues = {
  orientationAngle: number;
  altitudeAboveField: number;
};
type GetDetails = {
  original: CalibrationValues;
  current: CalibrationValues;
};

const INITIAL_VALUES: GetDetails = {
  original: {
    orientationAngle: 0,
    altitudeAboveField: 0,
  },
  current: {
    orientationAngle: 0,
    altitudeAboveField: 0,
  },
};

interface CalibrationProps {}

const Calibration: React.FC<CalibrationProps> = () => {
  const [savedValues, setSavedValues] = useState<GetDetails>(INITIAL_VALUES);
  const [loading, setLoading] = useState<boolean>(false);

  const guid = useAppSelector((state) => state.panorama.guid);
  const currentFieldId = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.fid
  );
  const currentFieldName = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.name
  );
  const fieldPanoImageId = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.field_pano_image_id
  );
  const imperialUnits = useAppSelector((state) => state.panorama.imperialUnits);

  const units: Parameters<typeof normaliseValue>[2] = imperialUnits
    ? "imperial"
    : "metric";

  useEffect(() => {
    const getDetails = async (
      params: Parameters<typeof CalibrationApi.getCalibration>[0]
    ) => {
      setLoading(true);
      const result = await CalibrationApi.getCalibration(params);
      setLoading(false);
      if (result.success && result.data) {
        setSavedValues({
          // Set "original" values
          original: {
            orientationAngle: roundNumber(
              result.data.original_image_orientation,
              1
            ),
            altitudeAboveField: roundNumber(
              result.data.original_height_above_take_off
            ),
          },
          // Set form's initial values (from "current")
          current: {
            orientationAngle: roundNumber(result.data.image_orientation, 1),
            altitudeAboveField: roundNumber(result.data.height_above_take_off),
          },
        });
      }
    };
    if (
      notNullNorUndefined(currentFieldId) &&
      notNullNorUndefined(guid) &&
      notNullNorUndefined(fieldPanoImageId)
    ) {
      getDetails({
        currentFieldID: currentFieldId,
        panoramaGuid: guid,
        panoImageId: fieldPanoImageId,
      });
    }
  }, [currentFieldId, guid, fieldPanoImageId]);

  return (
    <div className="PositionRelative DisplayFlex FlexColumn LargeGap">
      {/* Original Values */}
      <h6 className="MutedText">ORIGINAL</h6>
      <DataItem
        left="Orientation angle"
        middle={normaliseValue(
          savedValues.original.orientationAngle,
          "angle",
          units,
          " ([unit])"
        )}
      />
      <DataItem
        left="Height above take off"
        middle={normaliseValue(
          savedValues.original.altitudeAboveField,
          "distance",
          units,
          " ([unit])"
        )}
      />

      {/* Current Values */}
      <h6 className="MutedText">CURRENT</h6>
      <DataItem
        left="Orientation angle"
        middle={normaliseValue(
          savedValues.current.orientationAngle,
          "angle",
          units,
          " ([unit])"
        )}
      />
      <DataItem
        left="Height above take off"
        middle={normaliseValue(
          savedValues.current.altitudeAboveField,
          "distance",
          units,
          " ([unit])"
        )}
      />
      {/* New Values */}
      <h6 className="MutedText">CALIBRATE NEW VALUES</h6>
      <CalibrateForm
        initialOrientationAngle={savedValues.current.orientationAngle}
        initialDroneAltitude={savedValues.current.altitudeAboveField}
        //
        guid={guid}
        currentFieldId={currentFieldId}
        currentFieldName={currentFieldName}
        fieldPanoImageId={fieldPanoImageId}
        //
        loading={loading}
        setLoading={setLoading}
      />
      <Spinner show={loading} underlayClass="FaintUnderlay" />
    </div>
  );
};
export default Calibration;
